import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { apiClient } from '../services/api'

interface PrefRow {
  event_type: string
  in_app_enabled: boolean
  email_enabled: boolean
  digest_enabled: boolean
}

const EVENT_LABELS: Record<string, string> = {
  task_assigned: 'Task assigned to me',
  mention: '@mentions',
  deadline_tomorrow: 'Deadline tomorrow',
  milestone_approaching: 'Milestone approaching',
  change_request_pending: 'Approval requested',
  ai_insight_critical: 'Critical AI insight',
  governance_overdue: 'Governance overdue',
}

const COLUMNS: { key: keyof PrefRow; label: string }[] = [
  { key: 'in_app_enabled', label: 'In-app' },
  { key: 'email_enabled', label: 'Email' },
  { key: 'digest_enabled', label: 'Weekly digest' },
]

export default function NotificationPreferences() {
  const [rows, setRows] = useState<PrefRow[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState<string | null>(null)

  useEffect(() => {
    apiClient
      .get<PrefRow[]>('/api/v1/notifications/preferences')
      .then(r => setRows(r.data))
      .catch(() => toast.error('Failed to load preferences'))
      .finally(() => setLoading(false))
  }, [])

  const toggle = async (eventType: string, key: keyof PrefRow) => {
    const row = rows.find(r => r.event_type === eventType)
    if (!row) return
    const next = { ...row, [key]: !row[key] }
    setRows(prev => prev.map(r => (r.event_type === eventType ? next : r)))
    setSaving(eventType)
    try {
      await apiClient.put(`/api/v1/notifications/preferences/${eventType}`, {
        in_app_enabled: next.in_app_enabled,
        email_enabled: next.email_enabled,
        digest_enabled: next.digest_enabled,
      })
    } catch {
      toast.error('Failed to save')
      setRows(prev => prev.map(r => (r.event_type === eventType ? row : r)))
    } finally {
      setSaving(null)
    }
  }

  return (
    <div className="mx-auto max-w-3xl p-4 sm:p-6">
      <h1 className="text-xl font-semibold text-[var(--color-text-primary)] sm:text-2xl">
        Notification preferences
      </h1>
      <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
        Choose how you want to be notified for each event type.
      </p>

      {loading ? (
        <p className="mt-6 text-sm text-[var(--color-text-secondary)]">Loading…</p>
      ) : (
        <div className="mt-6 overflow-x-auto rounded-md border border-[var(--color-border)]">
          <table className="min-w-full text-sm">
            <thead className="bg-[var(--color-surface-hover)] text-left text-xs uppercase text-[var(--color-text-secondary)]">
              <tr>
                <th className="px-3 py-2">Event</th>
                {COLUMNS.map(c => (
                  <th key={c.key} className="px-3 py-2 text-center">
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={1 + COLUMNS.length}
                    className="p-4 text-center text-[var(--color-text-secondary)]"
                  >
                    No preferences configured. Defaults apply.
                  </td>
                </tr>
              ) : (
                rows.map(row => (
                  <tr
                    key={row.event_type}
                    className="border-t border-[var(--color-border)]"
                  >
                    <td className="px-3 py-3 text-[var(--color-text-primary)]">
                      {EVENT_LABELS[row.event_type] || row.event_type}
                    </td>
                    {COLUMNS.map(c => (
                      <td key={c.key} className="px-3 py-3 text-center">
                        <label className="inline-flex min-h-[44px] min-w-[44px] cursor-pointer items-center justify-center">
                          <input
                            type="checkbox"
                            checked={Boolean(row[c.key])}
                            disabled={saving === row.event_type}
                            onChange={() => toggle(row.event_type, c.key)}
                            className="h-5 w-5"
                          />
                        </label>
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
